<template>
  <div
    class="text-left text-dark-850 transition relative rounded-xl flex flex-col justify-center select-none"
    :class="[
      isVoters || isSelected ? '' : 'cursor-pointer hover:-translate-y-[1px] shadow-sm hover:shadow-md',

      isSelected || (voters.length >= 2 && isVoters) ? 'bg-royal-950/60 text-white' : 'bg-light-100/40 text-light-900 dark:bg-dark-950/40 dark:text-dark-50',
    ]"
  >
    <div class="w-12 h-full shrink-0 absolute duration-300 ease-out top-0 left-0 rounded-l-xl" :class="[ isSelected || (voters.length >= 2 && isVoters) ? 'bg-royal-800' : 'bg-white dark:bg-dark-700', isVoters && voters.length === 1 && voters[0]?._id === currentUser?._id ? '!bg-light-300 dark:!bg-dark-900' : '', ]" />

    <div class="absolute top-0 left-10 right-0 h-full">
      <div
        class="h-full rounded-r-xl duration-300 ease-out min-w-4"
        :class="[
          isSelected || (voters.length >= 2 && isVoters) ? 'bg-royal-800' : 'bg-white dark:bg-dark-700',

          isVoters && voters.length === 1 && voters[0]?._id === currentUser?._id ? '!bg-light-300 dark:!bg-dark-900' : '',
        ]"
        :style="{
          width: `${isResult ? (visualPercentage * 100) : 100}%`
        }"
      />
    </div>

    <div class="flex h-full py-1.5 pl-1.5 pr-2 space-x-2 items-center relative z-[1]">
      <div class="w-10 h-full min-h-10 flex items-center justify-center rounded-[0.6rem] shrink-0 text-dark-900" :class="[ isSelected || (voters.length >= 2 && isVoters) ? 'bg-royal-600' : 'bg-light-100 dark:bg-dark-600', isVoters && voters.length === 1 && voters[0]?._id === currentUser?._id ? '!bg-light-500 dark:!bg-dark-920' : '' ]">
        <IconBase name="cute/spinner" class="spin text-white opacity-50" v-if="isLoading" />
        <ImageEmoji :name="isVoters && isNotAnswered ? 'Red+Question+Mark' : option.emoji" size="sm" class="size-7" v-else-if="option.emoji" />
      </div>
      <div class="grow">
        <div class="font-bold text-3xs" v-if="isResult">
          <NumberFlow :value="Math.round((percentage * 100) * Math.pow(10, 1)) / Math.pow(10, 1)" :locales="$i18n.locale" :format="{ notation: 'standard' }" />% • <NumberFlow :value="option.count" :locales="$i18n.locale" :format="{ notation: 'compact' }" /> {{ $t('components.Quiz.option.voteCount', option.count)  }}
        </div>
        <div class="font-medium text-3xs line-clamp-1 break-all dark:text-dark-400 text-light-800" v-else-if="isVoters">
          <span class="text-royal-400" v-if="voters.length >= 2">
            {{ $t('components.Quiz.option.both') }}
          </span>
          <template v-else-if="voters[0] && voters[0]._id !== currentUser?._id">
            {{ $t('components.Quiz.option.theirs', { name: voters[0].name }) }}
          </template>
          <template v-else-if="voters[0] && voters[0]._id === currentUser?._id">
            {{ $t('components.Quiz.option.mine') }}
          </template>
        </div>

        <div
          class="font-semi"
          :class="[
            isResult ? 'line-clamp-1 break-all leading-[1.1]' : 'leading-[1]',
            isVoters && isNotAnswered ? 'blur-sm' : ''
          ]"
        >
          {{ isVoters && isNotAnswered ? textRandom(randomBetween(8, 20)) : option.text }}
        </div>
      </div>
      <div class="shrink-0 -space-x-1 flex items-center" v-if="isVoters || (isShowFaces && isResult)">
        <UserPicture
          v-for="voter in voters"
          :key="voter._id"
          is-hide-badges
          :user="voter"
          size="xs"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import { IQuizOption, IQuizQuestion } from '~/classes/Quiz'

const props = withDefaults(defineProps<{
  option: IQuizOption
  question: IQuizQuestion
  isNotAnswered?: boolean
  isSelected?: boolean
  isLoading?: boolean
  isVoters?: boolean
  isShowFaces?: boolean
  isResult?: boolean
}>(), {
  isResult: false,
  isSelected: false,
  isVoters: false,
  isNotAnswered: false,
  isShowFaces: false,
  isLoading: false,
  count: 0
})

const usersStore = UsersStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const voters = computed(() => props.isVoters || props.isShowFaces ? usersStore.getItems([ ...props.option.answered ?? [], ...(props.isSelected && currentUser.value ? [ currentUser.value._id ] : []) ]) : [])

const maxVotes = computed(() => {
  return Math.max(...props.question.options.map(opt => opt.count || 0))
})

const totalVotes = computed(() => {
  return props.question.options.reduce((acc, opt) => acc + (opt.count || 0), 0)
})

const visualPercentage = computed(() => {
  const maxWidth = percentage.value === 1 ? 1 : 0.95
  return (props.option.count || 0) / maxVotes.value * maxWidth
})

const percentage = computed(() => {
  if (totalVotes.value && props.option.count) return props.option.count / totalVotes.value
  return 0
})

</script>